import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { EventBus, PDFLinkService, PDFViewer } from 'pdfjs-dist/web/pdf_viewer';
import './pdf_viewer.css';
import './PdfFormViewer.css';

// The worker is copied to /public (same version as pdfjs-dist) so it loads without bundler config
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

export interface PdfFieldChange {
  field: string;
  before: string;
  after: string;
}

export interface PdfFormViewerHandle {
  // Fields whose value differs from what the PDF was opened with
  getChanges: () => PdfFieldChange[];
}

interface Props {
  url: string;
  editing: boolean;
}

interface FieldInfo {
  ids: string[];
  type: string;
  initial: string;
  exportValues: string[];
}

const asYesNo = (v: any) => (v === true || /^(on|yes|true|1)$/i.test(String(v)) ? 'Yes' : 'No');

// Renders the output PDF with its form fields. Fields are locked until `editing` is true;
// whatever the user types is kept in pdf.js' annotation storage and read back by getChanges().
const PdfFormViewer = forwardRef<PdfFormViewerHandle, Props>(({ url, editing }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<HTMLDivElement>(null);
  const pdfRef = useRef<any>(null);
  const fieldsRef = useRef<Record<string, FieldInfo>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useImperativeHandle(ref, () => ({
    getChanges: () => {
      const pdf = pdfRef.current;
      if (!pdf) return [];
      const storage: any = pdf.annotationStorage;
      const all: Record<string, any> =
        typeof storage.getAll === 'function' && storage.getAll()
          ? storage.getAll()
          : Object.fromEntries(storage.serializable || []);
      const changes: PdfFieldChange[] = [];
      Object.entries(fieldsRef.current).forEach(([name, info]) => {
        let after: string | null = null;
        if (info.type === 'checkbox') {
          const touched = info.ids.find((id) => all[id] && 'value' in all[id]);
          if (touched) after = asYesNo(all[touched].value);
        } else if (info.type === 'radiobutton') {
          const idx = info.ids.findIndex((id) => all[id] && all[id].value === true);
          if (idx >= 0) after = info.exportValues[idx] || 'Yes';
        } else {
          const touched = info.ids.find((id) => all[id] && 'value' in all[id]);
          if (touched) after = String(all[touched].value ?? '');
        }
        if (after !== null && after !== info.initial) {
          changes.push({ field: name, before: info.initial, after });
        }
      });
      return changes;
    },
  }));

  useEffect(() => {
    const container = containerRef.current;
    const viewerEl = viewerRef.current;
    if (!container || !viewerEl) return;

    let cancelled = false;
    setLoading(true);
    setError('');

    const eventBus = new EventBus();
    const linkService = new PDFLinkService({ eventBus });
    const viewer = new PDFViewer({
      container,
      viewer: viewerEl,
      eventBus,
      linkService,
      annotationMode: pdfjsLib.AnnotationMode.ENABLE_FORMS,
    } as any);
    linkService.setViewer(viewer);
    eventBus.on('pagesinit', () => {
      viewer.currentScaleValue = 'page-width';
      if (!cancelled) setLoading(false);
    });

    const task = pdfjsLib.getDocument({ url });
    task.promise
      .then(async (pdf: any) => {
        if (cancelled) return;
        pdfRef.current = pdf;
        viewer.setDocument(pdf);
        linkService.setDocument(pdf, null);

        const objects: Record<string, any[]> = (await pdf.getFieldObjects()) || {};
        const fields: Record<string, FieldInfo> = {};
        Object.entries(objects).forEach(([name, list]) => {
          const first = list[0] || {};
          fields[name] = {
            ids: list.map((o) => String(o.id)),
            type: first.type,
            initial: first.type === 'checkbox'
              ? asYesNo(first.value)
              : first.type === 'radiobutton' ? '' : String(first.value ?? ''),
            exportValues: list.map((o) => String(o.exportValues ?? '')),
          };
        });
        fieldsRef.current = fields;
      })
      .catch((e: any) => {
        if (cancelled) return;
        console.error('Error rendering output PDF:', e);
        setError('Unable to display the output file.');
        setLoading(false);
      });

    const onResize = () => {
      if (viewer.pdfDocument) viewer.currentScaleValue = 'page-width';
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
      pdfRef.current = null;
      fieldsRef.current = {};
      try { viewer.setDocument(null as any); } catch { /* viewer already torn down */ }
      task.destroy();
    };
  }, [url]);

  return (
    <div className={`pdf-form-viewer ${editing ? 'pdf-editing' : 'pdf-readonly'}`}>
      {loading && !error && <div className="pdf-form-loading">Loading output file...</div>}
      {error && <div className="pdf-form-loading">{error}</div>}
      <div className="pdf-form-container" ref={containerRef}>
        <div className="pdfViewer" ref={viewerRef} />
      </div>
    </div>
  );
});

PdfFormViewer.displayName = 'PdfFormViewer';

export default PdfFormViewer;
